import React, { useState, useEffect } from 'react';
import { useOrg } from '../../../context/OrgContext';
import { useNotification } from '../../../context/NotificationContext';
import { apiService } from '../../../services/apiService';
import { CafeStatus, StatusMessages } from '../../../types';
import './CafeStatusManager.css';

interface CafeStatusManagerProps {
  onStatusChange?: (status: CafeStatus) => void;
}

const CafeStatusManager: React.FC<CafeStatusManagerProps> = ({ onStatusChange }) => {
  const { currentOrganization } = useOrg();
  const { showNotification } = useNotification();
  const [cafeStatus, setCafeStatus] = useState<CafeStatus>({
    isOpen: false,
    isBusy: false
  });
  const [statusMessages, setStatusMessages] = useState<StatusMessages>({
    closed: "We're currently closed. Please visit us during our business hours.",
    busy: "We're experiencing high volume. Please visit our counter for faster service.",
    open: "Welcome! You can place your order below."
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentOrganization) {
      loadCafeStatus();
    }
  }, [currentOrganization]);

  const loadCafeStatus = async () => {
    if (!currentOrganization || !currentOrganization.urlName) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await apiService.getCafeStatus(currentOrganization.urlName);

      if (response.success && response.data) {
        setCafeStatus(response.data.cafeStatus);
        setStatusMessages(response.data.statusMessages);
      } else {
        const errorMsg = response.error || 'Failed to load cafe status';
        setError(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = err.message || 'An error occurred while loading cafe status';
      setError(errorMsg);
      console.error('Failed to load cafe status:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (updates: Partial<CafeStatus>) => {
    if (!currentOrganization) return;
    
    try {
      setLoading(true);
      setError('');
      
      const newStatus = { ...cafeStatus, ...updates };
      const response = await apiService.updateCafeStatus(currentOrganization.urlName, newStatus);
      
      if (response.success) {
        setCafeStatus(newStatus);
        onStatusChange?.(newStatus);
        
        // Show global notification with status change details
        const statusText = newStatus.isBusy ? 'Busy' : newStatus.isOpen ? 'Open' : 'Closed';
        showNotification('success', `Cafe status updated to: ${statusText}`, 4000);
      } else {
        const errorMsg = response.error || 'Failed to update cafe status';
        setError(errorMsg);
        showNotification('error', errorMsg, 5000);
      }
    } catch (err: any) {
      const errorMsg = err.message || 'An error occurred while updating cafe status';
      setError(errorMsg);
      showNotification('error', errorMsg, 5000);
    } finally {
      setLoading(false);
    }
  };

  const updateMessages = async (updates: Partial<StatusMessages>) => {
    if (!currentOrganization) return;
    
    try {
      setLoading(true);
      setError('');
      
      const newMessages = { ...statusMessages, ...updates };
      const response = await apiService.updateCafeStatus(currentOrganization.urlName, {}, newMessages);
      
      if (response.success) {
        setStatusMessages(newMessages);
        showNotification('success', 'Status messages updated successfully', 3000);
      } else {
        const errorMsg = response.error || 'Failed to update status messages';
        setError(errorMsg);
        showNotification('error', errorMsg, 5000);
      }
    } catch (err: any) {
      const errorMsg = err.message || 'An error occurred while updating status messages';
      setError(errorMsg);
      showNotification('error', errorMsg, 5000);
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = () => {
    if (cafeStatus.isBusy) return 'Busy';
    if (cafeStatus.isOpen) return 'Open';
    return 'Closed';
  };

  const getStatusColor = () => {
    if (cafeStatus.isBusy) return '#f59e0b'; // amber
    if (cafeStatus.isOpen) return '#10b981'; // green
    return '#ef4444'; // red
  };

  // Don't render if organization is not available
  if (!currentOrganization) {
    return null;
  }

  if (loading && !cafeStatus.isOpen && !cafeStatus.isBusy) {
    return (
      <div className="cafe-status-manager">
        <div className="loading-message">Loading cafe status...</div>
      </div>
    );
  }

  return (
    <div className="cafe-status-manager">
      <div className="status-header">
        <h3>Cafe Status</h3>
        <div className="current-status">
          <div 
            className="status-indicator" 
            style={{ backgroundColor: getStatusColor() }}
          ></div>
          <span className="status-text">{getStatusDisplay()}</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="status-controls">
        <div className="status-toggle">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={cafeStatus.isOpen}
              onChange={(e) => updateStatus({ isOpen: e.target.checked })}
              disabled={loading}
              className="toggle-input"
            />
            <span className="toggle-slider"></span>
            <span className="toggle-text">Cafe is Open</span>
          </label>
        </div>

        <div className="status-toggle">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={cafeStatus.isBusy}
              onChange={(e) => updateStatus({ isBusy: e.target.checked })}
              disabled={loading || !cafeStatus.isOpen}
              className="toggle-input"
            />
            <span className="toggle-slider"></span>
            <span className="toggle-text">Currently Busy</span>
          </label>
          {!cafeStatus.isOpen && (
            <small className="toggle-hint">Cafe must be open to set busy status</small>
          )}
        </div>
      </div>

      <div className="status-messages">
        <h4>Status Messages</h4>
        
        <div className="message-group">
          <label htmlFor="closed-message">Closed Message:</label>
          <textarea
            id="closed-message"
            value={statusMessages.closed}
            onChange={(e) => setStatusMessages({ ...statusMessages, closed: e.target.value })}
            onBlur={() => updateMessages({ closed: statusMessages.closed })}
            disabled={loading}
            className="message-textarea"
            rows={2}
          />
        </div>

        <div className="message-group">
          <label htmlFor="busy-message">Busy Message:</label>
          <textarea
            id="busy-message"
            value={statusMessages.busy}
            onChange={(e) => setStatusMessages({ ...statusMessages, busy: e.target.value })}
            onBlur={() => updateMessages({ busy: statusMessages.busy })}
            disabled={loading}
            className="message-textarea"
            rows={2}
          />
        </div>

        <div className="message-group">
          <label htmlFor="open-message">Open Message:</label>
          <textarea
            id="open-message"
            value={statusMessages.open}
            onChange={(e) => setStatusMessages({ ...statusMessages, open: e.target.value })}
            onBlur={() => updateMessages({ open: statusMessages.open })}
            disabled={loading}
            className="message-textarea"
            rows={2}
          />
        </div>
      </div>

      {cafeStatus.lastUpdated && (
        <div className="status-meta">
          <small>
            Last updated: {new Date(cafeStatus.lastUpdated).toLocaleString()}
            {cafeStatus.updatedBy && ` by ${cafeStatus.updatedBy}`}
          </small>
        </div>
      )}
    </div>
  );
};

export default CafeStatusManager;
